import { useAngularServices } from '@/react/components'
import { useEffect, useState } from 'react'
import { useRouter } from '@/react/hooks'
import { StartingData } from '@/react/types/observations'

interface Question {
  id: number
  category: number
  name: string
  date_created: string
  date_updated: string
  client: number
  is_active: boolean
  deleted: boolean
  order: number
  source: string
  plaintext_name: string
  custom_categories: number[]
  description: string | null
  reference_image: string | null
}

export interface Category {
  application: number
  client: number
  date_updated: string
  deleted: boolean
  id: number
  is_active: boolean
  name: string
  project_count: number
}

const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { Api, CurrentUser } = useAngularServices()
  const { stateService } = useRouter()

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await Api.get('categories', {
          application: stateService.params.app || '',
          client: CurrentUser.getClientId(),
          order: 'name',
          is_active: true,
          custom: true,
          page_size: 250,
        })
        const results = (response as any)?.data?.results
        setCategories(Array.isArray(results) ? results : [])
      } catch (err) {
        console.error('Error fetching categories:', err)
        setError('Failed to load categories')
      } finally {
        setIsLoading(false)
      }
    }

    void fetchCategories()
  }, [Api, CurrentUser, stateService.params.app])

  return { categories, isLoading, error }
}

const useQuestionData = (questionId: string | undefined) => {
  const [questionData, setQuestionData] = useState<Question | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { Api } = useAngularServices()

  useEffect(() => {
    if (!questionId) {
      setIsLoading(false)
      return
    }

    const fetchQuestion = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const response = await Api.get(`questions/${questionId}`, {})
        const data = (response as any)?.data
        setQuestionData(data || null)
      } catch (err) {
        console.error('Error fetching question:', err)
        setError('Failed to load question')
      } finally {
        setIsLoading(false)
      }
    }

    void fetchQuestion()
  }, [questionId, Api])

  return { questionData, isLoading, error }
}

const useStartingData = (
  questionData: Question | null,
  categories: Category[],
  previousCategoryId: number | null,
) => {
  const [startingData, setStartingData] = useState<StartingData | null>(null)

  useEffect(() => {
    if (!questionData || categories.length === 0) {
      return
    }

    const selectedCategory = categories.find(
      (c) => c.id === questionData.category,
    )

    const previousCategoryData = categories.find(
      (c) => c.id === previousCategoryId,
    )

    const categoryData = selectedCategory
      ? { name: selectedCategory.name, id: selectedCategory.id }
      : previousCategoryData
      ? { name: previousCategoryData.name, id: previousCategoryData.id }
      : { name: '', id: null }

    setStartingData({
      id: questionData.id,
      category: categoryData.name || '',
      categoryId: categoryData.id,
      editorContent: questionData.name || '',
      date_created: questionData.date_created,
      date_updated: questionData.date_updated,
    })
  }, [questionData, categories, previousCategoryId])

  return startingData
}

const useQuestionActions = () => {
  const [isMaxThresholdModal, setMaxThresholdModal] = useState<
    string | boolean
  >(false)
  const { Api, CurrentUser, Notification } = useAngularServices()
  const { stateService } = useRouter()

  const handleQuestionAdd = async (
    selectedCategory: Category,
    editorContent: string,
    startingData: StartingData,
  ) => {
    try {
      if (!startingData) {
        Notification.danger('Error: data not loaded')
        return
      }

      if (!startingData.categoryId) {
        Notification.danger(
          'Cannot save question. The category has been deleted.',
        )
        return
      }

      await Api.patch(`questions/${startingData.id}`, {
        category: selectedCategory?.id,
        client: CurrentUser.getClientId(),
        date_created: startingData?.date_created,
        date_updated: startingData?.date_updated,
        deleted: false,
        id: startingData?.id,
        is_active: true,
        name: editorContent,
        order: 0,
        source: 'internal',
      })

      // Navigate back to questions list
      try {
        stateService.go('app.questions.list', {
          categoryId: stateService.params.previousCategory,
          pageNumber: stateService.params.previousPageNumber,
          search: stateService.params.previousSearch,
          showBackButton: stateService.params.showBackButton,
        })
      } catch (navigationError) {
        console.error('Navigation error:', navigationError)
      }
    } catch (error) {
      Notification.clearNotification()
      if (
        error &&
        typeof error === 'object' &&
        'status' in error &&
        error.status === 400 &&
        'data' in error
      ) {
        setMaxThresholdModal(
          (error as { data: { detail: string } }).data.detail,
        )
      }
    }
  }

  return {
    isMaxThresholdModal,
    setMaxThresholdModal,
    handleQuestionAdd,
  }
}

const useHandleEditQuestions = () => {
  const { stateService } = useRouter()
  const { Notification } = useAngularServices()
  const [previousCategoryId, setPreviousCategoryId] = useState<number | null>(
    null,
  )

  useEffect(() => {
    const previousCategoryId = stateService.params.previousCategory
    if (previousCategoryId) {
      setPreviousCategoryId(+previousCategoryId)
    }
  }, [stateService.params.previousCategory])

  const {
    categories,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useCategories()
  const {
    questionData,
    isLoading: questionLoading,
    error: questionError,
  } = useQuestionData(stateService.params.question || undefined)

  const startingData = useStartingData(
    questionData,
    categories,
    previousCategoryId,
  )
  const questionActions = useQuestionActions()

  useEffect(() => {
    if (categoriesError) {
      Notification.danger(categoriesError)
    }
    if (questionError) {
      Notification.danger(questionError)
    }
  }, [categoriesError, questionError, Notification])

  const isLoading = categoriesLoading || questionLoading

  return {
    startingData,
    isLoading,
    categories,
    ...questionActions,
  }
}

export default useHandleEditQuestions
